import { z } from 'zod'

export const PLACE_HISTORY_TYPES = [
	'OPENED',
	'CLOSED',
	'RENAMED',
	'CHANGED_USE',
	'VISIT',
	'STATUS_UPDATE',
	'OTHER',
] as const

export const PLACE_HISTORY_DATE_PRECISIONS = [
	'DAY',
	'MONTH',
	'YEAR',
	'UNKNOWN',
] as const

export const PLACE_HISTORY_RESULTING_STATUSES = [
	'AT_RISK',
	'STABLE',
	'WORTH_SUPPORTING',
	'CLOSED',
	'UNKNOWN',
] as const

export const PLACE_HISTORY_TYPE_LABELS: Record<
	(typeof PLACE_HISTORY_TYPES)[number],
	string
> = {
	OPENED: 'Opened',
	CLOSED: 'Closed',
	RENAMED: 'Renamed',
	CHANGED_USE: 'Changed use',
	VISIT: 'Documented visit',
	STATUS_UPDATE: 'Status update',
	OTHER: 'Other',
}

const optionalText = z
	.string()
	.trim()
	.transform((value) => value || null)

const optionalHttpUrl = optionalText.refine(
	(value) => {
		if (!value) return true
		try {
			return ['http:', 'https:'].includes(new URL(value).protocol)
		} catch {
			return false
		}
	},
	{ message: 'Enter a valid http or https URL' },
)

const httpUrl = z
	.string()
	.url()
	.refine(
		(value) => {
			return ['http:', 'https:'].includes(new URL(value).protocol)
		},
		{ message: 'Enter a valid http or https URL' },
	)

export const PlaceHistoryEntryInputSchema = z
	.object({
		placeId: z.string().trim().min(1),
		importKey: z
			.string()
			.trim()
			.regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
			.nullable()
			.optional()
			.transform((value) => value || null),
		discoveryConfidence: z
			.number()
			.int()
			.min(0)
			.max(100)
			.nullable()
			.optional()
			.transform((value) => value ?? null),
		discoveryNotes: optionalText
			.pipe(z.string().max(2000).nullable())
			.optional()
			.transform((value) => value ?? null),
		additionalSources: z.array(httpUrl).max(12).default([]),
		type: z.enum(PLACE_HISTORY_TYPES),
		title: z.string().trim().min(3).max(160),
		description: optionalText.pipe(z.string().max(4000).nullable()),
		happenedAt: z.coerce.date().nullable(),
		happenedUntil: z.coerce.date().nullable(),
		datePrecision: z.enum(PLACE_HISTORY_DATE_PRECISIONS),
		isApproximate: z.boolean().default(false),
		placeNameAtTime: optionalText.pipe(z.string().max(160).nullable()),
		businessAtTime: optionalText.pipe(z.string().max(160).nullable()),
		resultingStatus: z
			.union([
				z.enum(PLACE_HISTORY_RESULTING_STATUSES),
				z.literal(''),
				z.null(),
			])
			.transform((value) => value || null),
		sourceUrl: optionalHttpUrl,
		mediaIds: z.array(z.string().trim().min(1)).default([]),
		publicationState: z.enum(['DRAFT', 'PUBLISHED']).default('DRAFT'),
		updateCurrentStatus: z.boolean().default(false),
	})
	.superRefine((value, context) => {
		if (value.datePrecision === 'UNKNOWN') {
			if (value.happenedAt || value.happenedUntil) {
				context.addIssue({
					code: 'custom',
					path: ['happenedAt'],
					message: 'Unknown dates cannot include a start or end date',
				})
			}
		} else if (!value.happenedAt) {
			context.addIssue({
				code: 'custom',
				path: ['happenedAt'],
				message: 'Enter a historical date for this precision',
			})
		}

		if (
			value.happenedAt &&
			value.happenedUntil &&
			value.happenedUntil < value.happenedAt
		) {
			context.addIssue({
				code: 'custom',
				path: ['happenedUntil'],
				message: 'The end date must not be before the start date',
			})
		}

		if (value.updateCurrentStatus && !value.resultingStatus) {
			context.addIssue({
				code: 'custom',
				path: ['resultingStatus'],
				message: 'Choose a resulting status before updating the current place',
			})
		}
	})

type HistoryDate = {
	happenedAt: Date | string | null
	happenedUntil: Date | string | null
	datePrecision: string
	isApproximate?: boolean
}

function asDate(value: Date | string) {
	return typeof value === 'string' ? new Date(value) : value
}

function formatOneDate(value: Date | string, precision: string) {
	const date = asDate(value)
	if (precision === 'YEAR') return String(date.getUTCFullYear())
	if (precision === 'MONTH') {
		return date.toLocaleDateString('en-US', {
			month: 'long',
			year: 'numeric',
			timeZone: 'UTC',
		})
	}
	return date.toLocaleDateString('en-US', {
		month: 'long',
		day: 'numeric',
		year: 'numeric',
		timeZone: 'UTC',
	})
}

export function formatPlaceHistoryDate(entry: HistoryDate) {
	if (entry.datePrecision === 'UNKNOWN' || !entry.happenedAt) {
		return 'Date unknown'
	}

	const start = formatOneDate(entry.happenedAt, entry.datePrecision)
	const range = entry.happenedUntil
		? `${start}–${formatOneDate(entry.happenedUntil, entry.datePrecision)}`
		: start
	return entry.isApproximate ? `Circa ${range}` : range
}
